import { NavLink, useLocation } from "react-router-dom";
import {
  SquaresFour,
  Receipt,
  Plus,
  Gear,
  List as ListIcon,
  UsersThree,
  SignOut,
  Tray,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { SettingsAPI, InvoiceAPI, ProfileAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import WelcomeDialog from "./WelcomeDialog";
import ProfileIncompleteBanner from "./ProfileIncompleteBanner";
import { Toaster } from "../components/ui/sonner";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const baseNav = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/invoices/new", label: "New Invoice", icon: Plus, testid: "nav-new-invoice" },
];

const adminExtras = [
  { to: "/submissions", label: "Submissions", icon: Tray, testid: "nav-submissions", badgeKey: "pending" },
  { to: "/subcontractors", label: "Subcontractors", icon: UsersThree, testid: "nav-subcontractors" },
];

const settingsNav = { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" };

const NavList = ({ onNavigate, isAdmin, badges }) => {
  const items = [...baseNav, ...(isAdmin ? adminExtras : []), settingsNav];
  return (
    <nav className="flex-1 p-3 space-y-1">
      {items.map((item) => {
        const Icon = item.icon;
        const count = item.badgeKey && badges ? badges[item.badgeKey] : 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors text-sm font-medium ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted"
              }`
            }
          >
            <Icon size={18} weight="bold" />
            <span className="flex-1">{item.label}</span>
            {count > 0 && (
              <span
                className="min-w-[20px] h-[20px] px-1.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-sm flex items-center justify-center"
                data-testid={`${item.testid}-badge`}
              >
                {count > 99 ? "99+" : count}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
};

const UserPanel = ({ user, onLogout }) => (
  <div className="p-4 border-t border-border space-y-2">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-primary text-primary-foreground font-display font-bold flex items-center justify-center rounded-sm text-xs shrink-0">
        {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate" data-testid="current-user-name">
          {user?.name || user?.email}
        </div>
        <div className="overline text-muted-foreground truncate">
          {user?.role}
        </div>
      </div>
    </div>
    <button
      onClick={onLogout}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
      data-testid="logout-btn"
    >
      <SignOut size={14} weight="bold" />
      Sign out
    </button>
  </div>
);

export default function Layout({ children }) {
  const [logoUrl, setLogoUrl] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ pending: 0 });
  const [profile, setProfile] = useState(null);
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSub = user?.role === "subcontractor";

  useEffect(() => {
    SettingsAPI.get()
      .then((s) => setLogoUrl(s.logo_url || ""))
      .catch(() => {});
  }, []);

  // Load subcontractor profile for welcome + completeness banner
  useEffect(() => {
    if (!isSub) return;
    ProfileAPI.get()
      .then(setProfile)
      .catch(() => {});
  }, [isSub, location.pathname]);

  // Poll pending submission count for admin
  useEffect(() => {
    if (!isAdmin) return;
    const fetchBadges = () => {
      InvoiceAPI.list()
        .then((list) => {
          const pending = list.filter(
            (i) => i.is_subcontractor_invoice && i.status === "Sent"
          ).length;
          setBadges({ pending });
        })
        .catch(() => {});
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 60000); // refresh every minute
    return () => clearInterval(t);
  }, [isAdmin, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 shrink-0 bg-white border-r border-border flex-col sticky top-0 h-screen"
        data-testid="main-sidebar"
      >
        <div className="px-6 py-6 border-b border-border">
          <Logo size="md" logoUrl={logoUrl} />
        </div>
        <NavList isAdmin={isAdmin} badges={badges} />
        <UserPanel user={user} onLogout={logout} />
      </aside>

      {/* Mobile top bar */}
      <header
        className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between bg-white border-b border-border px-4 py-3"
        data-testid="mobile-topbar"
      >
        <Logo size="sm" logoUrl={logoUrl} />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="p-2 hover:bg-muted rounded-sm"
              aria-label="Open menu"
              data-testid="mobile-menu-trigger"
            >
              <ListIcon size={22} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-0 flex flex-col" data-testid="mobile-drawer">
            <VisuallyHidden>
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>Main navigation menu</SheetDescription>
            </VisuallyHidden>
            <div className="px-5 py-5 border-b border-border">
              <Logo size="md" logoUrl={logoUrl} />
            </div>
            <NavList onNavigate={() => setMobileOpen(false)} isAdmin={isAdmin} badges={badges} />
            <UserPanel user={user} onLogout={logout} />
          </SheetContent>
        </Sheet>
      </header>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden pt-16 lg:pt-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          <ProfileIncompleteBanner user={user} profile={profile} />
          {children}
        </div>
      </main>

      <WelcomeDialog user={user} profile={profile} />
      <Toaster />
    </div>
  );
}
